import {useState, useEffect} from 'react';
import {useLocation, useRouteMatch} from 'react-router-dom';
import {ReactSVG} from 'react-svg';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {Alert, Collapse, Skeleton, Stack} from '@mui/material';
import {
  Business,
  PersonRounded,
  SettingsRounded,
  KeyboardArrowDown,
  Monitor,
  AutoAwesomeMosaic,
  WarningRounded,
} from '@mui/icons-material';
import {ListItemLink} from '../list-item-link/list-item-link';
import {useOffice} from '../../contexts/office/context';
import {ScreenPaths} from '../../enums';
import {ActivitiesSector} from '../../backend/interfaces/activities-sector';
import {useAuth} from '../../contexts/auth/context';
import {RolePermissionType} from '../../backend/enums/role-permission-type';
import {
  BADL_SECTOR_ID,
  CLINICAL_SUPPORT_SECTOR_ID,
  IADL_SECTOR_ID,
  PERSONAL_DEVELOPMENT_SECTOR_ID,
} from '../../utils/constants';
import PinIcon from '@mui/icons-material/Pin';
import {useCanViewEntitySettings} from '../../hooks/use-can-view-entity-settings';

// !!! NOTE: This assumes sectors are the same for all entities, as specified in the utils/constants/hardcoded.ts file.
const SECTOR_ID_TO_PERMISSION_TYPE_MAPPING: Record<string, RolePermissionType> = {
  [BADL_SECTOR_ID]: RolePermissionType.Settings_BADL,
  [IADL_SECTOR_ID]: RolePermissionType.Settings_IADL,
  [CLINICAL_SUPPORT_SECTOR_ID]: RolePermissionType.Settings_ClinicalSupport,
  [PERSONAL_DEVELOPMENT_SECTOR_ID]: RolePermissionType.Settings_PersonalDevelopment,
};

export interface InstitutionSidePanelComponentsProps {
  showExpanded?: boolean;
  activitiesSectors?: ActivitiesSector[] | null;
}

export const InstitutionSidePanelComponents = ({
  showExpanded: sidePanelExpanded,
  activitiesSectors,
}: InstitutionSidePanelComponentsProps) => {
  const [expandedInstitutionSettings, setExpandedInstitutionSettings] = useState(false);
  const officeContext = useOffice();
  const {t} = useTranslation();
  const {url} = useRouteMatch();
  const {pathname} = useLocation();
  const {hasReadPermission} = useAuth();
  const canViewEntitySettings = useCanViewEntitySettings();

  const skeletonSx = {
    borderRadius: '5px',
    mx: '12px',
    my: '8px',
  };

  // Main list styling
  const listItemsSx = {
    width: '180px',
    height: '40px',
    boxShadow: 'none',
    mx: '11px',
    px: '4px',
    '& .MuiTypography-root': {
      textOverflow: 'ellipsis',
      maxWidth: '100%',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      fontSize: '14px',
      fontWeight: 500,
    },
  };

  // Dropdown list styling
  const dropdownListItemsSx = {
    ...listItemsSx,
    color: '#747474',
    backgroundColor: '#F9FBFF',
    paddingLeft: sidePanelExpanded ? '22px' : '0px',
    paddingRight: '0px',
    '& .MuiTypography-root': {
      fontSize: '12px',
      fontWeight: 500,
      color: '#747474',
    },
    '& .MuiSvgIcon-root': {
      fontSize: '15px',
      color: '#747474',
    },
    '&:hover': {
      filter: 'brightness(95%)',
      backgroundColor: '#F9FBFF',
    },
  };

  const handleExpandClick = () => setExpandedInstitutionSettings(!expandedInstitutionSettings);

  useEffect(() => {
    const isInstitutionSettingsRoute = pathname.includes(
      ScreenPaths.ProfessionalCaregiver_InstitutionSettings
    );
    setExpandedInstitutionSettings(isInstitutionSettingsRoute);
  }, [pathname]);

  return (
    <>
      {(hasReadPermission(RolePermissionType.Settings_Entity_Profile) || canViewEntitySettings) && (
        <>
          <ListItemLink
            showTooltip={!sidePanelExpanded}
            to={`${url}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings}`}
            primary={t(translations.settingsSideMenu.institution)}
            icon={<Business className="list-item-link-icon" />}
            endIcon={
              <KeyboardArrowDown
                onClick={(e) => {
                  handleExpandClick();
                  e.preventDefault();
                }}
                sx={{rotate: expandedInstitutionSettings ? '180deg' : '0deg'}}
              />
            }
            expanded={sidePanelExpanded}
            teamSideMenuDropdown
            teamFirstChild
            sx={listItemsSx}
          />
          <Collapse
            in={expandedInstitutionSettings}
            collapsedSize={0}
            orientation="vertical"
            timeout={{enter: 500, exit: 500}}
          >
            {hasReadPermission(RolePermissionType.Settings_Entity_Profile) && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${url}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Profile}`}
                primary={t(translations.screen_paths.institution_settings_profile)}
                icon={<PersonRounded />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}
            {canViewEntitySettings && (
              <ListItemLink
                showTooltip={!sidePanelExpanded}
                to={`${url}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Settings}`}
                primary={t(translations.screen_paths.institution_settings_settings)}
                icon={<SettingsRounded />}
                teamProfileDropdown
                sx={dropdownListItemsSx}
              />
            )}
          </Collapse>
        </>
      )}

      {activitiesSectors === undefined && (
        <Stack gap="8px">
          {new Array(3).fill(1).map((_props, index) => (
            <Skeleton
              key={`skeleton_activity_sector_${index}`}
              animation="wave"
              variant="rectangular"
              width={officeContext.state.sideNavigationDrawerCollapsed ? 40 : 178}
              height={40}
              sx={skeletonSx}
            />
          ))}
        </Stack>
      )}

      {activitiesSectors === null && (
        <Alert severity="error">
          {t(translations.settingsSideMenu.activities_sectors.load_error)}
        </Alert>
      )}

      {activitiesSectors &&
        activitiesSectors.map(({id, name, iconUrl}: ActivitiesSector) => {
          const permissionType = SECTOR_ID_TO_PERMISSION_TYPE_MAPPING[id!];

          if (!permissionType || !hasReadPermission(permissionType)) {
            return null;
          }

          return (
            <ListItemLink
              showTooltip={!sidePanelExpanded}
              key={`activities-sector-link-${id}`}
              primary={name}
              to={`${url}${ScreenPaths.ProfessionalCaregiver_ActivitySectorSettings.replace(
                ':activitiesSectorId',
                id ?? ''
              )}`}
              icon={<ReactSVG className="list-item-link-icon" src={iconUrl} title={name} />}
              teamMiddleChild
              sx={listItemsSx}
            />
          );
        })}

      {hasReadPermission(RolePermissionType.Settings_Licenses) && (
        <ListItemLink
          showTooltip={!sidePanelExpanded}
          to={`${url}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Licenses}`}
          primary={t(translations.screen_paths.institution_settings_licenses)}
          icon={<Monitor />}
          teamMiddleChild
          sx={listItemsSx}
        />
      )}

      {hasReadPermission(RolePermissionType.Settings_Templates) && (
        <ListItemLink
          showTooltip={!sidePanelExpanded}
          to={`${url}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Templates}`}
          primary={t(translations.screen_paths.institution_settings_templates)}
          icon={<AutoAwesomeMosaic />}
          teamMiddleChild
          sx={listItemsSx}
        />
      )}

      {hasReadPermission(RolePermissionType.Settings_Occurrences) && (
        <ListItemLink
          showTooltip={!sidePanelExpanded}
          to={`${url}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_Occurrences}`}
          primary={t(translations.screen_paths.institution_settings_occurrences)}
          icon={<WarningRounded />}
          teamMiddleChild
          sx={listItemsSx}
        />
      )}

      {hasReadPermission(RolePermissionType.SettingsQuickAccessDevices) && (
        <ListItemLink
          showTooltip={!sidePanelExpanded}
          to={`${url}${ScreenPaths.ProfessionalCaregiver_InstitutionSettings_QuickAccessDevices}`}
          primary={t(translations.screen_paths.institution_settings_quick_access_devices)}
          icon={<PinIcon />}
          teamMiddleChild
          sx={listItemsSx}
        />
      )}
    </>
  );
};
